<script setup>
import { messageKey } from './domain'
import {
  applyTextChange,
  createTypingState,
  remainingFrames,
  shownText,
  tickTyping,
  typingInterval
} from './hangul'

const props = defineProps({
  log: { type: Array, default: () => [] },
  showMoreLabel: { type: String, default: 'Show more' },
  collapseLabel: { type: String, default: 'Collapse' }
})

const lastSpeech = computed(() => {
  for (let i = props.log.length - 1; i >= 0; i -= 1) {
    if (props.log[i].role !== 'system') return props.log[i]
  }
  return null
})

const dots = computed(() => lastSpeech.value === null || lastSpeech.value.role === 'user')
const fullText = computed(() => (dots.value ? '' : lastSpeech.value.message || ''))
const speechKey = computed(() => {
  const msg = lastSpeech.value
  if (!msg || msg.role === 'user') return ''
  return messageKey(msg)
})

const typed = ref('')
const expanded = ref(false)
const textRef = ref(null)
const lineRef = ref(null)
const typing = shallowRef(createTypingState(''))
const ratchet = reactive({ key: '', w: 0, h: 0 })
let typingTimer = null

const stopTyping = () => {
  if (typingTimer) clearInterval(typingTimer)
  typingTimer = null
}

const paint = (state) => {
  typing.value = state
  typed.value = shownText(state)
}

const startTyping = () => {
  stopTyping()
  if (remainingFrames(typing.value) === 0) return
  typingTimer = setInterval(() => {
    const next = tickTyping(typing.value)
    paint(next)
    if (remainingFrames(next) === 0) stopTyping()
  }, typingInterval(typing.value))
}

watch(fullText, (text) => {
  if (!text) {
    stopTyping()
    paint(createTypingState(''))
    return
  }
  paint(applyTextChange(typing.value, text, true))
  startTyping()
}, { immediate: true })

watch(speechKey, () => {
  expanded.value = false
})

const holdRatchet = computed(() => {
  if (dots.value) return false
  const isFinal = lastSpeech.value?.isFinal ?? true
  return !(isFinal && typed.value === fullText.value)
})

const measureRatchet = () => {
  if (ratchet.key !== speechKey.value) {
    ratchet.key = speechKey.value
    ratchet.w = 0
    ratchet.h = 0
  }
  const el = lineRef.value
  if (!holdRatchet.value || !el) return
  if (el.offsetWidth > ratchet.w) ratchet.w = el.offsetWidth
  if (el.offsetHeight > ratchet.h) ratchet.h = el.offsetHeight
}

watch([typed, holdRatchet, speechKey], () => {
  nextTick(measureRatchet)
})

let resizeObserver = null
watch(lineRef, (el) => {
  resizeObserver?.disconnect()
  resizeObserver = null
  if (!el || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(() => measureRatchet())
  resizeObserver.observe(el)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  stopTyping()
})

const ratchetStyle = computed(() => {
  if (!holdRatchet.value || ratchet.w === 0) return undefined
  return {
    minWidth: `${ratchet.w}px`,
    minHeight: `${ratchet.h}px`
  }
})

const overflow = computed(() => {
  const el = textRef.value
  if (!el || dots.value || typed.value !== fullText.value) return false
  return el.scrollHeight > el.clientHeight + 2
})
</script>

<template>
  <div class="ai-line-box">
    <div
      v-if="dots"
      class="ai-line dots"
    >
      <span class="ai-text">
        <i /><i /><i />
      </span>
    </div>
    <div
      v-else
      ref="lineRef"
      class="ai-line"
      :class="{ expanded, holding: holdRatchet }"
      :style="ratchetStyle"
    >
      <span
        ref="textRef"
        class="ai-text"
      >
        {{ typed }}
        <button
          v-if="overflow || expanded"
          type="button"
          class="ai-more"
          :aria-label="expanded ? collapseLabel : showMoreLabel"
          @click="expanded = !expanded"
        />
      </span>
    </div>
  </div>
</template>
